import { ApiError } from '../core/api/http'
import { readLocalJson, writeLocalJson } from '../core/storage'
import { nowStamp, shiftDate, today } from '../core/format/dates'
import { mailChecks, seriesChecks } from '../core/rules/mail'
import { matchesSegment, type SegmentProfile } from '../core/rules/segments'
import { knownSitePaths, redirectIssues } from '../core/rules/redirects'
import { db } from './db'
import { reply } from './reply'
import { buildSeedSubscribers, seedMailBrand, seedMailSeries, seedMailTemplates, seedSegments } from './seed/mail'
import { seedRedirects } from './seed/redirects'
import { localCatalog } from '../data/catalog'
import type { AdminBackend } from '../core/types/backend'
import type { AdminContent } from '../core/types/adminRequests'
import type { MailSettings, SegmentDefinition, Subscriber, SubscriberWeek } from '../core/types/mail'
import type { NewsletterDelivery } from '../core/types/newsletter'
import type { Redirect } from '../core/types/seo'
import type { SkinType } from '../core/types/catalog'

/**
 * DEMO — Mail & nieuwsbrief en doorverwijzingen, zoals de server het doet: versies (409),
 * dezelfde controles als de regels (422), idempotent op `requestId`. Eigen opslag naast demo/db.ts,
 * zodat deze tabellen los kunnen groeien.
 */

// v2: mail "Kuur verloopt bijna" (`kuur-expiring`) erbij; een oudere opgeslagen demo begint opnieuw.
const KEY = 'bb-demo-mail-v2'
const PAGE_SIZE = 25

type Store = {
  mails: MailSettings
  segments: { revision: number; items: SegmentDefinition[] }
  redirects: { revision: number; items: Redirect[] }
  /** Versturen en inplannen per nieuwsbrief-id (de inhoud zelf staat in het beheer-concept). */
  deliveries: Record<string, NewsletterDelivery>
  /** requestId → antwoord, voor herhalingen. */
  requests: Record<string, unknown>
}

const seedStore = (): Store => ({
  mails: { revision: 1, templates: seedMailTemplates, series: seedMailSeries, brand: seedMailBrand },
  segments: { revision: 1, items: seedSegments },
  redirects: { revision: 1, items: seedRedirects(today()) },
  deliveries: {
    'zomer-spf': {
      status: 'verzonden',
      scheduledAt: null,
      sentAt: `${shiftDate(today(), -54)}T09:30`,
      segmentName: 'Alle abonnees',
      stats: { recipients: 206, opened: 118, clicked: 31, unsubscribed: 2, bounced: 3, orders: 7, revenueCents: 41850 },
    },
  },
  requests: {},
})

function load(): Store {
  const s = readLocalJson<Store | null>(KEY, null)
  if (s && s.mails?.templates && s.segments?.items && s.redirects?.items && s.deliveries) return s
  const fresh = seedStore()
  writeLocalJson(KEY, fresh)
  return fresh
}
const save = (s: Store) => writeLocalJson(KEY, s)

const conflict = async (): Promise<never> => {
  await reply(null, 300)
  throw new ApiError('Dit is intussen ergens anders opgeslagen. Ververs de pagina.', 409, undefined, 'version_conflict')
}
const invalid = async (message: string, code?: string): Promise<never> => {
  await reply(null, 300)
  throw new ApiError(message, 422, undefined, code)
}

/** Eén keer uitvoeren per requestId; daarna hetzelfde antwoord. */
function once<T>(requestId: string, run: (s: Store) => T): T {
  const s = load()
  if (requestId in s.requests) return s.requests[requestId] as T
  const out = run(s)
  s.requests[requestId] = out
  save(s)
  return out
}

// ——— Abonnees ———

const skins: SkinType[] = ['Gevoelige huid', 'Droge huid', 'Rijpere huid', 'Pigmentatie', 'Hormonale huid', 'Vette huid']
const productIds = localCatalog.products.filter((p) => p.kind !== 'giftcard').map((p) => p.id)

type Row = Subscriber & { profile: SegmentProfile }

/** Voorbeeldabonnees, plus de demo-klanten met nieuwsbrief en de aanmeldingen uit de footer. */
function subscribers(): Row[] {
  const t = today()
  const seed: Row[] = buildSeedSubscribers(t, shiftDate).map((s, i) => ({
    ...s,
    profile: {
      isCustomer: i % 3 !== 2,
      productIds: i % 3 === 2 ? [] : [productIds[i % productIds.length], productIds[(i * 7) % productIds.length]],
      hasSubscription: i % 5 === 0,
      skinType: i % 4 === 3 ? null : skins[i % skins.length],
      lastVisit: i % 3 === 2 ? null : shiftDate(t, -((i * 23) % 200)),
    },
  }))
  const accounts: Row[] = db
    .accounts()
    .filter((a) => a.customer.prefs.newsletter && a.customer.email.trim() !== '')
    .map((a) => ({
      id: `sub-${a.customer.id}`,
      email: a.customer.email,
      firstName: a.customer.firstName,
      source: 'account',
      subscribedAt: a.customer.memberSince.slice(0, 10),
      confirmedAt: a.customer.memberSince.slice(0, 10),
      status: 'bevestigd',
      customerId: a.customer.id,
      profile: {
        isCustomer: a.orders.length > 0 || a.visits.length > 0,
        productIds: Array.from(new Set(a.orders.flatMap((o) => o.lines.map((l) => l.productId)))),
        hasSubscription: a.subscription?.status === 'actief',
        skinType: null,
        lastVisit: a.visits.map((v) => v.date).sort().at(-1) ?? null,
      },
    }))
  const known = new Set([...seed, ...accounts].map((s) => s.email.toLowerCase()))
  const signups: Row[] = db
    .newsletterSignups()
    .filter((s) => !known.has(s.email))
    .map((s, i) => ({
      id: `sub-footer-${i}`,
      email: s.email,
      firstName: null,
      source: 'footer',
      subscribedAt: s.at.slice(0, 10),
      // Pas na de klik op de bevestigingslink; daarvoor telt het adres in geen enkele verzending mee.
      confirmedAt: s.confirmedAt ? s.confirmedAt.slice(0, 10) : null,
      status: s.confirmedAt ? 'bevestigd' : 'onbevestigd',
      customerId: null,
      profile: { isCustomer: false, productIds: [], hasSubscription: false, skinType: null, lastVisit: null },
    }))
  return [...signups, ...accounts, ...seed].sort((a, b) => b.subscribedAt.localeCompare(a.subscribedAt))
}

/** Maandag van de week van `date`. */
function mondayOf(date: string) {
  const d = new Date(`${date}T12:00:00`)
  const back = (d.getDay() + 6) % 7
  return shiftDate(date, -back)
}

function weeks(rows: Row[]): SubscriberWeek[] {
  const start = mondayOf(shiftDate(today(), -7 * 11))
  return Array.from({ length: 12 }, (_, i) => {
    const week = shiftDate(start, i * 7)
    const end = shiftDate(week, 7)
    const inWeek = (d: string | null) => Boolean(d && d >= week && d < end)
    return {
      week,
      joined: rows.filter((r) => inWeek(r.subscribedAt)).length,
      // Afgemeld of onbestelbaar: de demo neemt de week na aanmelden als moment.
      left: rows.filter((r) => (r.status === 'afgemeld' || r.status === 'onbestelbaar') && inWeek(shiftDate(r.subscribedAt, 14))).length,
    }
  })
}

const confirmed = (rows: Row[]) => rows.filter((r) => r.status === 'bevestigd')

function countIn(rules: SegmentDefinition['rules']) {
  const t = today()
  return confirmed(subscribers()).filter((r) => matchesSegment(r.profile, rules, t)).length
}

const segmentList = (s: Store) => ({
  revision: s.segments.revision,
  items: s.segments.items.map((x) => ({ ...x, count: countIn(x.rules) })),
  total: confirmed(subscribers()).length,
})

/** De bestaande paden op de site, voor de waarschuwingen bij doorverwijzingen. */
const sitePaths = (content: AdminContent | null): Set<string> => knownSitePaths(content?.catalog ?? localCatalog)

const sitemapStatus = (paths: Set<string>) => ({ pages: paths.size, updatedAt: `${today()}T06:00` })

// ——— Nieuwsbrief: koppeling met het beheer-concept ———

/** Legt de stand van versturen over de opgeslagen nieuwsbrieven (zoals de server `delivery` meestuurt). */
export function withDeliveries(content: AdminContent): AdminContent {
  const { deliveries } = load()
  return {
    ...content,
    marketing: {
      ...content.marketing,
      newsletters: (content.marketing.newsletters ?? []).map((n) => ({ ...n, delivery: deliveries[n.id] ?? null })),
    },
  }
}

/** Zoals de server: wat het beheer meestuurt aan `delivery` telt niet. */
export const stripDeliveries = (content: AdminContent): AdminContent => ({
  ...content,
  marketing: { ...content.marketing, newsletters: (content.marketing.newsletters ?? []).map(({ delivery: _d, ...n }) => n) },
})

/**
 * Zoals de server: een ingeplande of verzonden nieuwsbrief mag via PUT /admin/content niet meer
 * inhoudelijk veranderen (409 `newsletter_locked`). Verwijderen van een ingeplande evenmin.
 */
export function lockedNewsletterChanged(stored: AdminContent | null, next: AdminContent): boolean {
  const { deliveries } = load()
  const before = new Map((stored?.marketing.newsletters ?? []).map(({ delivery: _d, ...n }) => [n.id, JSON.stringify(n)]))
  const after = new Map((next.marketing.newsletters ?? []).map(({ delivery: _d, ...n }) => [n.id, JSON.stringify(n)]))
  return Object.entries(deliveries).some(([id, d]) => d && before.has(id) && before.get(id) !== after.get(id))
}

function savedIssue(id: string, content: AdminContent | null) {
  return content?.marketing.newsletters?.find((n) => n.id === id) ?? null
}

export function demoMailBackendFor(savedContent: () => AdminContent | null) {
  const backend: Pick<
    AdminBackend,
    | 'fetchRedirects'
    | 'saveRedirects'
    | 'fetchMailSettings'
    | 'saveMailSettings'
    | 'sendMailTest'
    | 'sendNewsletter'
    | 'scheduleNewsletter'
    | 'sendNewsletterTest'
    | 'fetchSubscribers'
    | 'fetchSegments'
    | 'saveSegments'
  > = {
    fetchRedirects() {
      const s = load()
      return reply({ ...s.redirects, sitemap: sitemapStatus(sitePaths(savedContent())) }, 300)
    },

    async saveRedirects({ revision, items }) {
      const s = load()
      if (revision !== s.redirects.revision) return conflict()
      const paths = sitePaths(savedContent())
      const errors = redirectIssues(items, (p) => paths.has(p)).flatMap((list) => list.filter((i) => i.level === 'error'))
      if (errors.length) return invalid(`Niet opgeslagen: ${errors[0].text.toLowerCase()}.`, 'redirect_invalid')
      s.redirects = { revision: revision + 1, items }
      save(s)
      return reply({ ...s.redirects, sitemap: sitemapStatus(paths) }, 400)
    },

    fetchMailSettings: () => reply(load().mails, 300),

    async saveMailSettings(next) {
      const s = load()
      if (next.revision !== s.mails.revision) return conflict()
      // De standaardtekst is van Laravel: nooit overnemen uit het beheer.
      const byKey = new Map([...s.mails.templates, ...s.mails.series].map((m) => [m.key, m.standard]))
      const templates = next.templates.map((m) => ({ ...m, enabled: m.required ? true : m.enabled, standard: byKey.get(m.key) ?? m.standard }))
      const series = next.series.map((m) => ({ ...m, standard: byKey.get(m.key) ?? m.standard }))
      const bad = [...templates.flatMap((m) => mailChecks(m)), ...series.flatMap((m) => seriesChecks(m))].find((c) => c.level === 'error')
      if (bad) return invalid(`Niet opgeslagen: ${bad.text.toLowerCase()}.`, 'mail_invalid')
      if (!next.brand.footer.address.trim()) return invalid('Het adres in de voettekst is verplicht.', 'mail_invalid')
      s.mails = { ...next, templates, series, revision: next.revision + 1 }
      save(s)
      return reply(s.mails, 450)
    },

    async sendMailTest(key, { requestId }) {
      const known = [...load().mails.templates, ...load().mails.series].some((m) => m.key === key)
      if (!known) {
        await reply(null, 250)
        throw new ApiError('Deze mail bestaat niet.', 404)
      }
      return reply(once(requestId, () => ({ ok: true as const })), 600)
    },

    async sendNewsletter(id, { requestId }) {
      const s = load()
      if (requestId in s.requests) return reply(s.requests[requestId] as { id: string; delivery: NewsletterDelivery | null }, 300)
      const issue = savedIssue(id, savedContent())
      if (!issue) return invalid('Sla de nieuwsbrief eerst op; de server verstuurt altijd de opgeslagen versie.', 'newsletter_unsaved')
      if (s.deliveries[id]?.status === 'verzonden') {
        await reply(null, 300)
        throw new ApiError('Deze nieuwsbrief is al verstuurd.', 409, undefined, 'newsletter_locked')
      }
      if (!issue.subject.trim() || issue.blocks.length === 0) return invalid('De nieuwsbrief is nog niet compleet.', 'newsletter_incomplete')
      const segment = s.segments.items.find((x) => x.id === issue.segmentId)
      const recipients = segment ? countIn(segment.rules) : confirmed(subscribers()).length
      const delivery: NewsletterDelivery = {
        status: 'verzonden',
        scheduledAt: null,
        sentAt: nowStamp().slice(0, 16),
        segmentName: segment?.name ?? 'Alle abonnees',
        // Net verstuurd: de cijfers komen pas binnen via de webhooks van Brevo.
        stats: { recipients, opened: 0, clicked: 0, unsubscribed: 0, bounced: 0, orders: 0, revenueCents: 0 },
      }
      const out = { id, delivery }
      s.deliveries[id] = delivery
      s.requests[requestId] = out
      save(s)
      return reply(out, 900)
    },

    async scheduleNewsletter(id, { requestId, sendAt }) {
      const s = load()
      if (requestId in s.requests) return reply(s.requests[requestId] as { id: string; delivery: NewsletterDelivery | null }, 300)
      const issue = savedIssue(id, savedContent())
      if (!issue) return invalid('Sla de nieuwsbrief eerst op; de server verstuurt altijd de opgeslagen versie.', 'newsletter_unsaved')
      if (s.deliveries[id]?.status === 'verzonden') {
        await reply(null, 300)
        throw new ApiError('Deze nieuwsbrief is al verstuurd.', 409, undefined, 'newsletter_locked')
      }
      let delivery: NewsletterDelivery | null = null
      if (sendAt) {
        if (sendAt <= nowStamp().slice(0, 16)) return invalid('Kies een moment in de toekomst.', 'send_at_past')
        const segment = s.segments.items.find((x) => x.id === issue.segmentId)
        delivery = { status: 'ingepland', scheduledAt: sendAt, sentAt: null, segmentName: segment?.name ?? 'Alle abonnees', stats: null }
        s.deliveries[id] = delivery
      } else {
        delete s.deliveries[id]
      }
      const out = { id, delivery }
      s.requests[requestId] = out
      save(s)
      return reply(out, 500)
    },

    async sendNewsletterTest(id, { requestId }) {
      if (!savedIssue(id, savedContent())) return invalid('Sla de nieuwsbrief eerst op; de testmail gebruikt de opgeslagen versie.', 'newsletter_unsaved')
      return reply(once(requestId, () => ({ ok: true as const })), 600)
    },

    fetchSubscribers({ q, status, page = 1 }) {
      const all = subscribers()
      const needle = q?.trim().toLowerCase() ?? ''
      const hits = all.filter(
        (r) => (!status || r.status === status) && (!needle || r.email.toLowerCase().includes(needle) || (r.firstName ?? '').toLowerCase().includes(needle)),
      )
      const pageCount = Math.max(1, Math.ceil(hits.length / PAGE_SIZE))
      const p = Math.min(Math.max(1, page), pageCount)
      const weekAgo = shiftDate(today(), -7)
      return reply(
        {
          items: hits.slice((p - 1) * PAGE_SIZE, p * PAGE_SIZE).map(({ profile: _p, ...r }) => r),
          page: p,
          pageCount,
          total: hits.length,
          stats: {
            confirmed: all.filter((r) => r.status === 'bevestigd').length,
            pending: all.filter((r) => r.status === 'onbevestigd').length,
            unsubscribed: all.filter((r) => r.status === 'afgemeld').length,
            bounced: all.filter((r) => r.status === 'onbestelbaar').length,
            joinedLastWeek: all.filter((r) => r.subscribedAt >= weekAgo).length,
            weeks: weeks(all),
          },
        },
        350,
      )
    },

    fetchSegments: () => reply(segmentList(load()), 300),

    async saveSegments({ revision, items }) {
      const s = load()
      if (revision !== s.segments.revision) return conflict()
      if (items.some((x) => !x.name.trim())) return invalid('Elke doelgroep heeft een naam nodig.', 'segment_invalid')
      s.segments = { revision: revision + 1, items }
      save(s)
      return reply(segmentList(s), 400)
    },
  }
  return backend
}
